import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Volume2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { useStudentMe } from '../../modules/roster/hooks/useStudentMe';
import { useWordOfDay } from '../../modules/learning/hooks/useWordOfDay';
import { speakTamil } from '../../modules/learning/utils/tts';
import { WordOfDayPracticeModal } from './WordOfDayPracticeModal';
import { VocabularyWordImage } from './VocabularyWordImage';

type Props = {
  lessonNo?: number | null;
  /** Flat text on hero banner (no card chrome). */
  variant?: 'overlay' | 'banner';
  /** Responsive placement — single mount for desktop + mobile hero. */
  className?: string;
};

export function WordOfDayHeroPanel({ lessonNo, variant = 'overlay', className }: Props) {
  const { t } = useTranslation();
  const [practiceOpen, setPracticeOpen] = useState(false);
  const { grade, isRosterLoading } = useStudentMe();
  const studentGrade =
    Number.isFinite(Number(grade)) && (grade as number) >= 1 && (grade as number) <= 12
      ? Number(grade)
      : undefined;
  const { data: wotd, isLoading } = useWordOfDay(studentGrade, lessonNo);

  const playWord = useCallback(() => {
    if (!wotd) return;
    if (wotd.word_tts_url) void new Audio(wotd.word_tts_url).play();
    else speakTamil(wotd.word);
  }, [wotd]);

  if (isRosterLoading || isLoading || !wotd) return null;

  const canPractice = Boolean(wotd.flashcard_id || wotd.vocabulary_entry_id);
  const isBanner = variant === 'banner';

  return (
    <>
      <div
        className={
          className ??
          (isBanner
            ? 'flex flex-col items-center justify-center text-center gap-1 sm:gap-1.5'
            : 'shrink-0 w-full sm:w-[11.5rem] md:w-[13rem] rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 p-3 sm:p-3.5 flex flex-col items-center text-center gap-2')
        }
      >
        <p
          className={
            isBanner
              ? 'text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-app-primary'
              : 'text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-white/90'
          }
        >
          🌟 Word of the Day
        </p>
        <VocabularyWordImage
          flashcardId={wotd.flashcard_id}
          vocabularyEntryId={wotd.vocabulary_entry_id}
          imageUrl={wotd.image_url}
          hasImage={wotd.has_image}
          category={wotd.category || 'plant'}
          className={
            isBanner
              ? 'h-12 w-12 sm:h-14 sm:w-14 object-contain rounded-lg bg-white/90 p-1'
              : 'h-12 w-12 sm:h-14 sm:w-14 object-contain rounded-lg bg-white/90 p-1'
          }
          emojiClassName={isBanner ? 'text-3xl leading-none' : 'text-3xl leading-none'}
          alt={wotd.meaning_en || wotd.word}
        />
        <div className="flex items-center justify-center gap-1">
          <p
            className={
              isBanner
                ? 'text-base sm:text-lg font-bold tamil-text text-app-primary leading-tight'
                : 'text-xl sm:text-2xl font-bold tamil-text text-white leading-tight'
            }
          >
            {wotd.word}
          </p>
          <button
            type="button"
            className={
              isBanner
                ? 'p-0.5 text-app-primary hover:text-app-primary transition-colors'
                : 'p-1 rounded-lg hover:bg-white/20 transition-colors'
            }
            onClick={playWord}
            aria-label={t('dashboard.studentHome.listenToWord')}
          >
            <Volume2
              className={
                isBanner ? 'w-4 h-4 text-app-primary' : 'w-4 h-4 sm:w-5 sm:h-5 text-white'
              }
            />
          </button>
        </div>
        <p
          className={
            isBanner
              ? 'text-[11px] sm:text-xs font-medium text-app-primary line-clamp-2'
              : 'text-xs sm:text-sm font-medium text-white/95 line-clamp-2'
          }
        >
          {wotd.meaning_en}
        </p>
        {canPractice && (
          isBanner ? (
            <button
              type="button"
              className="mt-0.5 inline-flex items-center gap-1 text-[11px] sm:text-xs font-semibold text-app-primary hover:text-app-primary underline-offset-2 hover:underline"
              onClick={() => setPracticeOpen(true)}
            >
              <Mic className="w-3.5 h-3.5" />
              {t('dashboard.studentHome.practice')}
            </button>
          ) : (
            <Button
              type="button"
              size="sm"
              className="mt-0.5 h-8 px-3 text-xs !bg-white/95 !text-app-primary hover:!bg-white border-0 gap-1"
              onClick={() => setPracticeOpen(true)}
            >
              <Mic className="w-3.5 h-3.5" />
              {t('dashboard.studentHome.practice')}
            </Button>
          )
        )}
      </div>
      {studentGrade !== undefined && (
        <WordOfDayPracticeModal
          isOpen={practiceOpen}
          onClose={() => setPracticeOpen(false)}
          word={wotd}
          grade={studentGrade}
        />
      )}
    </>
  );
}
